/**
 * RodeoListItem — Delegate rodeo list row (accordion pattern).
 *
 * Purpose
 *   - Render a single rodeo as a bordered card with clickable header and
 *     expandable body, visually consistent with `JackpotListRow`.
 *   - Fully CONTROLLED: expand/collapse state is owned by the parent page
 *     (typically `DelegateRodeosPage`) so the page can enforce single-open
 *     accordion behavior or any other policy. Unlike `JackpotListRow`, this
 *     component intentionally holds NO local `expanded` state.
 *   - Delegate the trash action up to the parent via `onDelete(id)` — the
 *     actual confirmation dialog lives at the page level so it can be reused
 *     across rows and share the `useDeleteRodeo` mutation.
 *
 * Expanded body
 *   - When `isExpanded === true`, renders a nested slot for the forthcoming
 *     `PerformanceList` component. This is a placeholder today; the slot API
 *     is intentionally closed (no additional props) so `PerformanceList` can
 *     land without changing the row's props contract.
 *   - Indentation and the `border-t` separator mirror how jackpot events
 *     expand inside `JackpotListRow`.
 *
 * Actions
 *   - Pencil icon navigates to `/delegate/rodeos/${rodeo.id}/edit` (the
 *     existing `RodeoWizard` edit route).
 *   - Trash icon calls `onDelete(rodeo.id)`. The row never mutates data itself.
 *   - Both actions stop click propagation so they do not trigger the
 *     header's `onToggle`.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  Pencil,
  Trash2,
  Calendar,
  CalendarX,
  RotateCcw,
  Trophy,
  Target,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatDateRange } from '@/utils/formatters';
import {
  RodeoMasterPrizeToggles,
  type PrizeToggleField,
} from '@/components/delegate/RodeoMasterPrizeToggles';
import { useEventsList } from '@/api/generated_hooks/generated-events-hooks';
import { useApplyRodeoPrizeToggle } from '@/features/prize-management/prizeDisplayToggles';
import {
  useUpdateRodeos,
  rodeosKeys,
} from '@/api/generated_hooks/generated-rodeos-hooks';
import {
  rodeoOpsKeys,
  useSetRodeoCancellation,
} from '@/api/extensions/useRodeoOps';
import { useEventsPrizeTotal, type PrizeEventLike } from '@/api/extensions/usePrizeOps';
import { EventSection } from '@/components/delegate/EventSection';

export interface RodeoListItemProps {
  /**
   * Rodeo record from `useRodeosList`. Typed loosely (`any`) to match the
   * loose `Rodeo` interface returned by the hook. Known fields consumed:
   *   - id, name, is_published, start_date, end_date
   */
  rodeo: any;
  /** Called when the user clicks the trash icon. Parent opens confirmation. */
  onDelete: (id: string) => void;
  /** Controlled expand state. Parent owns this value. */
  isExpanded: boolean;
  /** Called when the user clicks the header (chevron / name area). */
  onToggle: () => void;
}

export function RodeoListItem({
  rodeo,
  onDelete,
  isExpanded,
  onToggle,
}: RodeoListItemProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isPublished = !!rodeo.is_published;
  const isCancelled = !!rodeo.cancelled_at;
  const updateRodeoMutation = useUpdateRodeos();
  const setCancellation = useSetRodeoCancellation();
  const { applyRodeoToggle, isSaving: bulkPrizeSaving } = useApplyRodeoPrizeToggle();

  const handleTogglePublish = async (next: boolean) => {
    try {
      await updateRodeoMutation.mutateAsync({
        id: rodeo.id,
        data: { is_published: next } as any,
      });
      // Invalidate both the generated CRUD cache and the rodeo-ops list
      // (the delegate page pulls from the ops endpoint for scoping).
      queryClient.invalidateQueries({ queryKey: rodeosKeys.all });
      queryClient.invalidateQueries({ queryKey: rodeoOpsKeys.all });
    } catch {
      // Swallow — the mutation surfaces its own error via React Query
      // and the next refetch will re-sync the toggle to the DB truth.
    }
  };

  // Cancelling is confirmed inline (window.confirm) rather than via a modal:
  // it is reversible from this same button, and the destructive part — the
  // refunds — is a separate, reviewed bulk step that this never triggers.
  const handleToggleCancelled = async () => {
    const next = !isCancelled;
    const question = next
      ? `Cancel "${rodeo.name}"?\n\nIts entries will stop owing fees (so nobody gets blocked from future rodeos), and entries already in the draw become cancellable. No refunds are issued by this action.`
      : `Reinstate "${rodeo.name}"?\n\nIts unpaid entries will owe their fees again.`;
    if (!window.confirm(question)) return;
    const reason = next
      ? window.prompt('Reason (optional) — shown on refund records:') ?? null
      : null;
    try {
      await setCancellation.mutateAsync({
        rodeoId: rodeo.id,
        cancelled: next,
        reason,
      });
    } catch {
      // Swallow — React Query surfaces the error and the next refetch
      // re-syncs the badge to DB truth (same pattern as the publish toggle).
    }
  };

  // Load the rodeo's events from the unified `events` table — same source
  // contestant pages read from. Reading events directly keeps admin and
  // contestant views in sync by construction.
  const { data: eventsPage, isLoading: eventsLoading } =
    useEventsList({
      filters: { rodeo_id: rodeo.id },
      limit: 100,
    } as any);

  const events: any[] = useMemo(() => {
    // The generated CRUD hook returns either a paginated envelope with
    // `.data` or a bare array, depending on plugin config — normalize.
    const raw = eventsPage as any;
    const list: any[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
    // Stable alphabetical sort by displayed title. The generated CRUD
    // endpoint returns rows in whatever order Postgres feels like (no
    // ORDER BY on the list query), so any UPDATE that touches a row
    // shuffles it to a fresh position when PostgREST re-sorts by ctid.
    // Sorting client-side keeps the list stable across edits and gives
    // delegates an alphabetical view that matches what they see on the
    // public event detail pages. Falls back to the rodeo-level event
    // type label when event_name is blank (matches getEventDisplayTitle).
    return [...list].sort((a, b) => {
      const titleOf = (e: any) =>
        (typeof e?.event_name === 'string' && e.event_name.trim()) ||
        (typeof e?.trig_event_type_name === 'string' && e.trig_event_type_name) ||
        '';
      return titleOf(a).localeCompare(titleOf(b), undefined, { sensitivity: 'base' });
    });
  }, [eventsPage]);

  // Bulk-apply one prize-display toggle to every event in this rodeo. The
  // Results → Live dependency invariant and the only-write-what-changed diff
  // live in the shared module, so this surface and the Prize Management tab
  // cannot drift into writing different rows.
  const applyMasterPrizeToggle = (field: PrizeToggleField, target: boolean) =>
    applyRodeoToggle(events, field, target);

  // Header prize pool — sum each event's AFTER-adjustment computed total (the
  // same net/live figure each expanded event row shows), not the raw
  // pre-adjustment `total_prize_amount` (which is 0 for dynamic events and so
  // badly undercounts a dynamic rodeo). `requireDisplay: false` makes this sum
  // every event regardless of the per-event contestants toggle, matching the
  // rows in the expanded list (which are not gated on that toggle).
  const totalPrize = useEventsPrizeTotal(events as PrizeEventLike[], {
    requireDisplay: false,
  });
  const headerStats = useMemo(
    () => ({ eventCount: events.length, totalPrize }),
    [events.length, totalPrize],
  );

  return (
    <div className="rounded-lg bg-card text-card-foreground overflow-hidden shadow-sm border border-border">
      {/* Header row — clickable, fires onToggle */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none bg-muted/80 hover:bg-muted transition-colors"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />

        {/* Name + publish switch + date range */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-sm truncate">{rodeo.name}</h3>
            {/* Inline publish toggle — flips immediately on change (no
                confirm, no extra save click). stopPropagation on the
                wrapper keeps clicks from toggling the accordion. */}
            <span
              className="flex items-center gap-1.5 shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Switch
                checked={isPublished}
                disabled={updateRodeoMutation.isPending}
                onCheckedChange={handleTogglePublish}
                aria-label={isPublished ? 'Unpublish rodeo' : 'Publish rodeo'}
              />
              <Badge
                variant={isPublished ? 'default' : 'secondary'}
                className={`text-xs ${
                  isPublished
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
              >
                {isPublished ? 'Published' : 'Draft'}
              </Badge>
            </span>
            {isCancelled ? (
              <Badge
                variant="destructive"
                className="text-xs shrink-0"
                title={
                  rodeo.cancellation_reason ||
                  'This rodeo is cancelled. Its entries owe nothing and can be cancelled even after the draw.'
                }
              >
                Cancelled
              </Badge>
            ) : null}
            {rodeo.is_finals === true ? (
              <Badge
                variant="outline"
                className="text-xs shrink-0"
                title="Finals rodeo: every contestant runs in every performance their event's rounds are tied to."
              >
                Finals
              </Badge>
            ) : null}
          </div>

          {/* Subtitle row: date range + aggregated stats (events + prize pool) */}
          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
            {(rodeo.start_date || rodeo.end_date) && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateRange(rodeo.start_date, rodeo.end_date)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Target className="h-3 w-3" />
              {headerStats.eventCount === 1
                ? '1 event'
                : `${headerStats.eventCount} events`}
            </span>
            {headerStats.totalPrize > 0 && (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                <Trophy className="h-3 w-3" />
                ${headerStats.totalPrize.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
                })} prize pool
              </span>
            )}
          </div>
        </div>

        {/* Master prize toggles + action buttons — stopPropagation so flipping
            a master switch or clicking an action doesn't toggle the row. */}
        <div
          className="flex items-center gap-3 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Rodeo-wide master switches for the three per-event prize toggles,
              each showing an on/total count. Hidden when the rodeo has no
              events (the component returns null). */}
          <RodeoMasterPrizeToggles
            idPrefix={`rodeo-${rodeo.id}`}
            events={events}
            onApply={applyMasterPrizeToggle}
            disabled={bulkPrizeSaving || eventsLoading}
          />
          <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            title="Edit"
            onClick={() => navigate(`/delegate/rodeos/${rodeo.id}/edit`)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          {/* Cancel / reinstate the whole rodeo. Separate from unpublishing:
              unpublished just hides it, cancelled means "this is not
              happening" and stops its entries owing anything. */}
          <Button
            variant="ghost"
            size="icon"
            className={`h-7 w-7 ${isCancelled ? 'text-green-600 hover:text-green-700' : 'text-destructive hover:text-destructive'}`}
            title={
              isCancelled
                ? 'Reinstate this rodeo — its entries will owe their fees again'
                : 'Cancel this rodeo — entries stop owing fees and can be cancelled even after the draw'
            }
            disabled={setCancellation.isPending}
            onClick={handleToggleCancelled}
          >
            {isCancelled ? (
              <RotateCcw className="h-4 w-4" />
            ) : (
              <CalendarX className="h-4 w-4" />
            )}
          </Button>
          {/* Trash icon only shows when the rodeo is safely deletable:
              not published, draw not initialized, and no signups. The
              backend surfaces `can_delete` on each rodeo and re-enforces
              the rule on DELETE, so a hidden icon is purely cosmetic. */}
          {rodeo.can_delete === true && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:text-destructive"
              title="Delete"
              onClick={() => onDelete(rodeo.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          </div>
        </div>
      </div>

      {/* Expanded body — lists the rodeo's events. Each event row is
          itself an accordion that expands to show its own per-event
          performances. Matches the product model where a single
          performance runs exactly one discipline. */}
      {isExpanded && (
        <div className="border-t px-4 py-3 space-y-2">
          {eventsLoading ? (
            <p className="text-sm text-muted-foreground italic">
              Loading events…
            </p>
          ) : events.length > 0 ? (
            events.map((ev) => (
              <EventSection
                key={ev.id}
                event={ev}
                rodeoId={rodeo.id}
                rodeo={rodeo}
              />
            ))
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No events added yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default RodeoListItem;
